import { PRIORITY_LABELS, offsetText, type Priority, type Task } from './types';
import { NumberInput } from '../ui/NumberInput';
import { parseDecimal } from '../ui/numbers';

/** A task being edited: `key` is local (React), `id` comes from the server for tasks that already exist. */
export type DraftTask = { key: string; id?: string; title: string; offset: string; priority: Priority };

let counter = 0;
const nextKey = () => `t${++counter}`;

export const toDraftTask = (t: Task): DraftTask => ({ key: nextKey(), ...(t.id ? { id: t.id } : {}), title: t.title, offset: String(t.dueOffsetDays), priority: t.priority });

/** Furthest a task may be from the first day of the event (the server's limit too). */
export const MAX_OFFSET_DAYS = 365;

/** Whole number of days (at most a year either way), or null when the field holds anything else. A "−" typed as a dash is accepted. */
export function parseOffset(text: string): number | null {
  const t = text.trim().replace(/^[–]/, '-');
  if (t === '') return 0;
  const days = parseDecimal(t, { negative: true, maxIntegerDigits: 3 });
  if (days === null) return null;
  return Math.abs(days) <= MAX_OFFSET_DAYS ? days : null;
}

/** What the server gets; null when a task has an empty title or a bad term (the form says which). */
export function tasksToWire(tasks: DraftTask[]): Task[] | null {
  const out: Task[] = [];
  for (const t of tasks) {
    const days = parseOffset(t.offset);
    if (t.title.trim() === '' || days === null) return null;
    out.push({ ...(t.id ? { id: t.id } : {}), title: t.title.trim(), dueOffsetDays: days, priority: t.priority });
  }
  return out;
}

/**
 * The task list of a function: one row per task (title, term in days from the first day of the event, priority),
 * ↑ / ↓ to change the order, "Usuń". Saved together with the function.
 */
export function FunctionTasksEditor({ tasks, onChange, canEdit }: { tasks: DraftTask[]; onChange: (tasks: DraftTask[]) => void; canEdit: boolean }) {
  const set = (i: number, patch: Partial<DraftTask>) => onChange(tasks.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  const move = (i: number, by: -1 | 1) => {
    const next = [...tasks];
    const [t] = next.splice(i, 1);
    next.splice(i + by, 0, t!);
    onChange(next);
  };
  return (
    <section className="fn-tasks" aria-label="Lista zadań" data-feature-id="people.function_tasks">
      <h3>Lista zadań</h3>
      <p className="muted">Zadania dostanie każda osoba przypisana do wydarzenia w tej funkcji. Termin to dni od pierwszego dnia wydarzenia: −7 = tydzień przed, 0 = w dniu rozpoczęcia.</p>
      {tasks.length === 0 && <p className="muted">Ta funkcja nie ma jeszcze zadań.</p>}
      <ol className="fn-task-list">
        {tasks.map((t, i) => {
          const n = i + 1;
          const days = parseOffset(t.offset);
          return (
            <li key={t.key} className="fn-task">
              <label className="field fn-task-title">
                <span>Zadanie {n}</span>
                <input value={t.title} onChange={(e) => set(i, { title: e.target.value })} readOnly={!canEdit} required maxLength={300} placeholder="np. Zebrać rider od zespołu" autoComplete="off" />
              </label>
              <label className="field fn-task-offset">
                <span aria-hidden="true">Termin (dni)</span>
                <NumberInput
                  aria-label={`Termin: zadanie ${n}`}
                  kind="integer"
                  inputMode="text"
                  value={t.offset}
                  onValueChange={(v) => set(i, { offset: v })}
                  readOnly={!canEdit}
                  aria-invalid={days === null}
                  aria-describedby={`fn-offset-${t.key}`}
                />
                <small id={`fn-offset-${t.key}`} className={days === null ? 'field-error' : 'muted'}>
                  {days === null ? `Wpisz liczbę dni od −${MAX_OFFSET_DAYS} do ${MAX_OFFSET_DAYS}, np. −7` : offsetText(days)}
                </small>
              </label>
              <label className="field fn-task-priority">
                <span aria-hidden="true">Priorytet</span>
                <select aria-label={`Priorytet: zadanie ${n}`} value={t.priority} disabled={!canEdit} onChange={(e) => set(i, { priority: e.target.value as Priority })}>
                  {(Object.keys(PRIORITY_LABELS) as Priority[]).map((p) => (
                    <option key={p} value={p}>
                      {PRIORITY_LABELS[p]}
                    </option>
                  ))}
                </select>
              </label>
              {canEdit && (
                <div className="fn-task-tools">
                  <button type="button" className="btn icon" aria-label={`W górę: zadanie ${n}`} disabled={i === 0} onClick={() => move(i, -1)}>
                    ↑
                  </button>
                  <button type="button" className="btn icon" aria-label={`W dół: zadanie ${n}`} disabled={i === tasks.length - 1} onClick={() => move(i, 1)}>
                    ↓
                  </button>
                  <button type="button" className="btn small ghost" aria-label={`Usuń: zadanie ${n}`} onClick={() => onChange(tasks.filter((_, j) => j !== i))}>
                    Usuń
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ol>
      {canEdit && (
        <button type="button" className="btn small" onClick={() => onChange([...tasks, { key: nextKey(), title: '', offset: '0', priority: 'normalny' }])}>
          Dodaj zadanie
        </button>
      )}
    </section>
  );
}
